// i18n.js — 言語の判定と文字列の取り出し
//
// 言語は URL のサブパスで決まる（/ja/ /ko/ …）。ビルド時に言語ごとの
// index.html を書き出しているので、静的なホストにそのまま置ける。
// / に来た人は判定して飛ばす（vite.config.js のプラグイン）。
//
// 言語を足すには LOCALES に 1 行と js/locales/<id>.js を足すだけでよい。
// 鍵が抜けていれば既定（日本語）に落ちるので、訳が揃っていなくても動く。

/**
 * dir  … 配信するサブパス。URL に出るのでここは変えない
 * tags … ブラウザの言語タグ。長いものから順に照合する
 */
export const LOCALES = [
  { id: 'ja', dir: 'ja', label: '日本語', html: 'ja', tags: ['ja'] },
  { id: 'en', dir: 'en', label: 'English', html: 'en', tags: ['en'] },
  { id: 'ko', dir: 'ko', label: '한국어', html: 'ko', tags: ['ko'] },
  {
    id: 'zh-Hans', dir: 'zh-Hans', label: '简体中文', html: 'zh-Hans',
    tags: ['zh-hans', 'zh-cn', 'zh-sg', 'zh-my', 'zh'],
  },
  {
    id: 'zh-Hant', dir: 'zh-Hant', label: '繁體中文', html: 'zh-Hant',
    tags: ['zh-hant', 'zh-tw', 'zh-hk', 'zh-mo'],
  },
];

export const DEFAULT_LOCALE = 'ja';

/** 選んだ言語を覚えておく鍵。/ に来たときの判定より優先する */
export const PREF_KEY = 'pachinkorokoro.lang';

const CATALOGS = {
  ja: () => import('./locales/ja.js'),
  en: () => import('./locales/en.js'),
  ko: () => import('./locales/ko.js'),
  'zh-Hans': () => import('./locales/zh-Hans.js'),
  'zh-Hant': () => import('./locales/zh-Hant.js'),
};

export const findLocale = (id) => LOCALES.find((l) => l.id === id) || null;

/**
 * ブラウザの言語から一番近いものを選ぶ。
 * zh は zh-TW と zh-CN で字体が違うので、長いタグから先に照合する
 * （そうしないと zh-TW が総称の zh に吸われて簡体字になる）。
 */
export function detectLocale(langs) {
  const list = langs || (typeof navigator !== 'undefined'
    ? (navigator.languages || [navigator.language]) : []);
  const pairs = [];
  for (const l of LOCALES) for (const tag of l.tags) pairs.push([tag, l.id]);
  pairs.sort((a, b) => b[0].length - a[0].length);

  for (const raw of list) {
    if (!raw) continue;
    const tag = String(raw).toLowerCase();
    for (const [t, id] of pairs) {
      if (tag === t || tag.startsWith(t + '-')) return id;
    }
  }
  return DEFAULT_LOCALE;
}

/** URL のどこかに言語のディレクトリがあればそれを使う */
export function localeFromPath(pathname) {
  const parts = String(pathname ?? (typeof location !== 'undefined' ? location.pathname : ''))
    .split('/').filter(Boolean);
  if (parts.length && /\.html?$/i.test(parts[parts.length - 1])) parts.pop();
  const last = (parts[parts.length - 1] || '').toLowerCase();
  return LOCALES.find((l) => l.dir.toLowerCase() === last)?.id || null;
}

/** 別の言語の同じ場所への行き先。今いる言語のディレクトリを差し替える */
export function localeHref(id) {
  const dir = findLocale(id)?.dir || DEFAULT_LOCALE;
  const parts = location.pathname.split('/').filter(Boolean);
  if (parts.length && /\.html?$/i.test(parts[parts.length - 1])) parts.pop();
  const last = (parts[parts.length - 1] || '').toLowerCase();
  if (LOCALES.some((l) => l.dir.toLowerCase() === last)) parts.pop();
  return '/' + [...parts, dir, ''].join('/');
}

/**
 * 言語ディレクトリの外にある共有素材（動画など）の URL。
 *
 * 動画は言語に関係ないので dist/videos/ に 1 つだけ置いてある。一方ページは
 * /ja/ /ko/ … と 1 階層下にあるので、'videos/wait.mp4' をそのまま <video> に
 * 渡すと /ko/videos/wait.mp4 を取りに行ってしまう。開発サーバも preview も
 * 見つからない要求には index.html を 200 で返すため、404 にすらならず
 * 「HTML を動画として再生しようとして黙って失敗する」形になる。
 *
 * @param {string} path 'videos/wait.mp4' のようなサイト根からの相対パス
 * @param {string} [href] 判定に使う URL（試験用。既定は今いるページ）
 */
export function assetUrl(path, href) {
  const base = href || (typeof location !== 'undefined' ? location.href : '');
  if (!base) return path;
  const clean = String(path).replace(/^\.?\//, '');
  const up = localeFromPath(new URL(base).pathname) ? '../' : './';
  return new URL(up + clean, base).href;
}

// ---------------------------------------------------------------- 文字列

let current = DEFAULT_LOCALE;
let strings = {};
let fallback = {};

/**
 * 文字列を読み込む。既定の言語ぶんも一緒に持っておき、訳の抜けを埋める。
 * 言語ごとに別のチャンクになるので、使う 1 言語ぶんしか落ちてこない。
 */
export async function loadLocale(id) {
  const use = CATALOGS[id] ? id : DEFAULT_LOCALE;
  strings = (await CATALOGS[use]()).default;
  fallback = use === DEFAULT_LOCALE ? strings : (await CATALOGS[DEFAULT_LOCALE]()).default;
  current = use;
  return use;
}

/** いま使っている言語 */
export const locale = () => current;

/** その言語で数を書くときの区切り。1,000 / 1 000 などの違いを吸収する */
export const num = (n) => Number(n).toLocaleString(current);

/**
 * 鍵から文字列を取り出す。{name} の形の差し込みができる。
 * 鍵が無いときは鍵そのものを返す。画面に出るので抜けにすぐ気づける。
 */
export function t(key, vars) {
  let s = strings[key];
  if (s === undefined) s = fallback[key];
  if (s === undefined) return key;
  if (Array.isArray(s)) s = s[0] ?? key;
  if (vars) {
    for (const [k, v] of Object.entries(vars)) s = s.split(`{${k}}`).join(String(v));
  }
  return s;
}

/** 台詞のように複数行を持つ鍵 */
export function tList(key) {
  const v = strings[key] !== undefined ? strings[key] : fallback[key];
  if (Array.isArray(v)) return v;
  return v === undefined ? [] : [v];
}

/** 台詞の束から 1 つ選ぶ。中身が配列の配列なら、その 1 本を返す */
export function tPick(key, rng) {
  const v = strings[key] !== undefined ? strings[key] : fallback[key];
  if (!Array.isArray(v) || !v.length) return [];
  const one = v[Math.floor((rng ? rng() : Math.random()) * v.length) % v.length];
  return Array.isArray(one) ? one : [one];
}

/** 訳が入っているか（鍵の抜けを数える。npm test から使う） */
export function hasKey(key) {
  return strings[key] !== undefined;
}
